import { useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, Download, Scale, X } from "lucide-react";
import { api, API, money } from "@/lib/api";
import { Pph21Report, Pph23Report, PphBadan, TaxSettings } from "./TaxReports";

const REPORTS = {
  pl: { label: "Laba Rugi", desc: "Menampilkan semua pendapatan yang diperoleh dan biaya yang dikeluarkan dalam periode tertentu." },
  bs: { label: "Neraca", desc: "Menampilkan apa yang dimiliki (aset), apa saja utangnya (liabilitas), dan yang sudah diinvestasikan (ekuitas) pada tanggal tertentu." },
  tb: { label: "Neraca Saldo", desc: "Menampilkan saldo dari setiap akun, termasuk pergerakan debit dan kredit dalam periode tertentu." },
  gl: { label: "Buku Besar", desc: "Menampilkan semua transaksi berdasarkan akun dalam periode tertentu, termasuk kronologi pergerakan transaksinya." },
  cf: { label: "Arus Kas", desc: "Menampilkan pergerakan uang masuk dan keluar dari transaksi dalam periode tertentu (operasional, investasi, pendanaan)." },
  ec: { label: "Perubahan Modal", desc: "Menampilkan perubahan atau pergerakan ekuitas pemilik dalam periode tertentu, termasuk laba ditahan dan laba periode berjalan." },
  jr: { label: "Jurnal", desc: "Menampilkan semua journal entry per transaksi dalam periode tertentu. Anda dapat melacak transaksi yang masuk ke masing-masing akun." },
  sales: { label: "Penjualan per Pelanggan", desc: "Menampilkan rekap invoice penjualan per pelanggan: jumlah transaksi, total, terbayar, dan sisa tagihan." },
  purchases: { label: "Pembelian per Supplier", desc: "Menampilkan rekap tagihan pembelian per supplier: jumlah transaksi, total, terbayar, dan sisa hutang." },
  ppn: { label: "SPT Masa PPN", desc: "Rekap PPN Keluaran vs PPN Masukan per masa pajak untuk persiapan pelaporan SPT Masa PPN (e-Faktur / Coretax DJP)." },
  pph21: { label: "PPh 21 Karyawan", desc: "Hitung PPh 21 karyawan otomatis dengan metode TER (PP 58/2023) berdasarkan status PTKP, seperti KlikPajak — rekap per masa & ekspor bukti potong." },
  pph23: { label: "PPh 23", desc: "Catat pemotongan PPh 23 atas jasa, sewa, dividen, bunga, royalti, dan hadiah — tarif mengikuti Pengaturan Pajak, rekap per masa & ekspor CSV." },
  badan: { label: "PPh Badan / Final UMKM", desc: "Estimasi PPh tahunan perusahaan dari laporan Laba Rugi: tarif PPh Badan, PPh Final UMKM 0,5%, atau progresif orang pribadi." },
  taxset: { label: "Pengaturan Pajak", desc: "Atur jenis wajib pajak (Badan / UMKM / Orang Pribadi) serta besaran tarif PPh Badan, PPh Final, PPN, dan tarif pemotongan PPh 23." },
};
const CATEGORIES = [
  { id: "bisnis", label: "Sekilas bisnis", reports: ["bs", "gl", "pl", "jr", "cf", "tb", "ec"] },
  { id: "penjualan", label: "Penjualan", reports: ["sales"] },
  { id: "pembelian", label: "Pembelian", reports: ["purchases"] },
  { id: "bank", label: "Bank", reports: ["gl", "cf"] },
  { id: "pajak", label: "Pajak", reports: ["ppn", "pph21", "pph23", "badan", "taxset"] },
];
const yearStart = `${new Date().getFullYear()}-01-01`;
const today = new Date().toISOString().slice(0, 10);
const PRESETS = [
  { id: "today", label: "Hari Ini" },
  { id: "week", label: "Minggu Ini" },
  { id: "month", label: "Bulan Ini" },
  { id: "last7", label: "7 Hari Terakhir" },
  { id: "last30", label: "30 Hari Terakhir" },
  { id: "year", label: "Tahun Ini" },
  { id: "all", label: "Semua Waktu" },
];
const isoDate = (x) => x.toISOString().slice(0, 10);
function presetRange(id) {
  const d = new Date();
  const end = isoDate(d);
  switch (id) {
    case "today": return [end, end];
    case "week": { const day = (d.getDay() + 6) % 7; const mon = new Date(d); mon.setDate(d.getDate() - day); return [isoDate(mon), end]; }
    case "month": return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`, end];
    case "last7": { const s = new Date(d); s.setDate(d.getDate() - 6); return [isoDate(s), end]; }
    case "last30": { const s = new Date(d); s.setDate(d.getDate() - 29); return [isoDate(s), end]; }
    case "year": return [`${d.getFullYear()}-01-01`, end];
    case "all": return ["2000-01-01", end];
    default: return null;
  }
}
const DATED = { pl: "range", tb: "range", gl: "range", cf: "range", sales: "range", purchases: "range", bs: "asof", ec: "range", jr: "range" };
const PDF_KEYS = { pl: "profit-loss", bs: "balance-sheet", tb: "trial-balance", cf: "cash-flow", ec: "equity-changes", jr: "journal", gl: "general-ledger" };
const SUBTITLES = {
  pph21: "Perhitungan otomatis memakai tabel TER resmi (PP 58/2023 & PMK 168/2023) sesuai status PTKP.",
  pph23: "Tarif pemotongan mengikuti Pengaturan Pajak; penerima tanpa NPWP dikenai tarif lebih tinggi.",
  badan: "Estimasi dari laporan Laba Rugi tahun berjalan, sebelum koreksi fiskal.",
  taxset: "Pengaturan tarif pajak berlaku untuk seluruh perusahaan ini.",
};

export default function Reports({ initialTab = null }) {
  const [tab, setTab] = useState(initialTab);
  const [category, setCategory] = useState("bisnis");
  const [start, setStart] = useState(yearStart);
  const [end, setEnd] = useState(today);
  const [preset, setPreset] = useState("year");
  const applyPreset = (id) => { const r = presetRange(id); if (r) { setStart(r[0]); setEnd(r[1]); } setPreset(id); };
  const [glAccount, setGlAccount] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const openLedger = (code) => { setGlAccount(code); setTab("gl"); };
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const params = new URLSearchParams();
      if (DATED[tab] === "range") params.set("start", start);
      params.set("end", end);
      if (tab === "gl") {
        if (!glAccount) throw new Error("Pilih akun terlebih dahulu untuk PDF Buku Besar");
        params.set("account_code", glAccount);
      }
      const r = await fetch(`${API}/reports/pdf/${PDF_KEYS[tab]}?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || "Gagal membuat PDF");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${REPORTS[tab].label.replaceAll(" ", "-")}-${end}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { alert(e.message); }
    setPdfBusy(false);
  };
  if (!tab) {
    const cat = CATEGORIES.find((c) => c.id === category);
    return (
      <div className="reports-page">
        <div className="page-heading">
          <div><p className="eyebrow">LAPORAN</p><h1>Laporan</h1></div>
        </div>
        <div className="report-tabs" data-testid="report-category-tabs">
          {CATEGORIES.map((c) => <button key={c.id} data-testid={`report-category-${c.id}`} className={category === c.id ? "active" : ""} onClick={() => setCategory(c.id)}>{c.label}</button>)}
        </div>
        <div className="report-hub" data-testid="report-hub">
          {cat.reports.map((id) => (
            <div key={id} className="report-hub-card" data-testid={`report-card-${id}`}>
              <h3>{REPORTS[id].label}</h3>
              <p>{REPORTS[id].desc}</p>
              <button data-testid={`report-open-${id}`} className="secondary-btn small" onClick={() => setTab(id)}>Lihat laporan</button>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="reports-page">
      <div className="page-heading">
        <div>
          <button data-testid="report-back-button" className="text-btn report-back" onClick={() => setTab(null)}><ArrowLeft size={14} /> Semua laporan</button>
          <h1>{REPORTS[tab]?.label}</h1>
          <p className="muted">{SUBTITLES[tab] || "Angka dihitung langsung dari jurnal yang telah diposting."}</p>
        </div>
        <div className="report-head-right">
          {PDF_KEYS[tab] && (
            <button data-testid="report-download-pdf" className="secondary-btn small" disabled={pdfBusy} onClick={downloadPdf}>
              <Download size={14} /> {pdfBusy ? "Menyiapkan..." : "Unduh PDF"}
            </button>
          )}
          {DATED[tab] && (
            <div className="report-range" data-testid="report-date-range">
              {DATED[tab] === "range" && (
                <label className="report-preset">Periode
                  <select data-testid="report-preset-select" value={preset} onChange={(e) => applyPreset(e.target.value)}>
                    {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    <option value="custom">Custom</option>
                  </select>
                </label>
              )}
              {DATED[tab] === "range" && <label>Dari<input data-testid="report-start-input" type="date" value={start} onChange={(e) => { setStart(e.target.value); setPreset("custom"); }} /></label>}
              <label>{DATED[tab] === "asof" ? "Per tanggal" : "Sampai"}<input data-testid="report-end-input" type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPreset("custom"); }} /></label>
            </div>
          )}
        </div>
      </div>
      {tab === "pl" && <ProfitLoss start={start} end={end} onDrill={openLedger} />}
      {tab === "bs" && <BalanceSheet asOf={end} onDrill={openLedger} />}
      {tab === "tb" && <TrialBalance start={start} end={end} onDrill={openLedger} />}
      {tab === "gl" && <GeneralLedger start={start} end={end} account={glAccount} setAccount={setGlAccount} />}
      {tab === "cf" && <CashFlow start={start} end={end} />}
      {tab === "ec" && <EquityChanges start={start} end={end} onDrill={openLedger} />}
      {tab === "jr" && <JournalReport start={start} end={end} />}
      {tab === "sales" && <PartySummary path="sales-by-customer" start={start} end={end} partyKey="customer" partyLabel="PELANGGAN" testid="sales-report" />}
      {tab === "purchases" && <PartySummary path="purchases-by-supplier" start={start} end={end} partyKey="supplier" partyLabel="SUPPLIER" testid="purchases-report" />}
      {tab === "ppn" && <TaxPPN />}
      {tab === "pph21" && <Pph21Report />}
      {tab === "pph23" && <Pph23Report />}
      {tab === "badan" && <PphBadan />}
      {tab === "taxset" && <TaxSettings />}
    </div>
  );
}

function useReport(path, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setError("");
    api(path).then((d) => active && setData(d)).catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, deps); // eslint-disable-line
  return [data, error];
}

function ReportRow({ row, onDrill, testPrefix }) {
  return (
    <button className="report-row" data-testid={`${testPrefix}-row-${row.code}`} onClick={() => onDrill(row.code)}>
      <span><em>{row.code}</em>{row.name}</span>
      <strong>{money(row.amount)}</strong>
      <ChevronRight size={14} />
    </button>
  );
}

const fmtAmt = (v) => {
  const s = new Intl.NumberFormat("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(v || 0));
  return v < 0 ? `(${s})` : s;
};
const fmtDMY = (d) => (d ? d.split("-").reverse().join("/") : "");

function MekSection({ label }) {
  return <tr className="mek-group"><td colSpan={2}>{label}</td></tr>;
}
function MekSub({ label }) {
  return <tr className="mek-sub"><td colSpan={2}>{label}</td></tr>;
}
function MekRow({ row, onDrill, prefix }) {
  return (
    <tr className="mek-row" data-testid={`${prefix}-row-${row.code}`} onClick={() => row.code && onDrill(row.code)}>
      <td><span className="mek-code">{row.code}</span><span className={row.code ? "mek-link" : "mek-static"}>{row.name}</span></td>
      <td className="right mono">{fmtAmt(row.amount)}</td>
    </tr>
  );
}
function MekTotal({ label, value, level = 1, testid }) {
  return (
    <tr className={`mek-total lv${level}`} data-testid={testid}>
      <td>{label}</td>
      <td className="right mono">{fmtAmt(value)}</td>
    </tr>
  );
}

function ProfitLoss({ start, end, onDrill }) {
  const [data, error] = useReport(`/reports/profit-loss?start=${start}&end=${end}`, [start, end]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Menghitung laba rugi...</div>;
  const s = data.sections, st = data.section_totals;
  return (
    <div className="surface workflow-table-wrap" data-testid="profit-loss-report">
      <table className="workflow-table mek-table">
        <thead><tr><th>NAMA AKUN</th><th className="right">{fmtDMY(start)} — {fmtDMY(end)}</th></tr></thead>
        <tbody>
          <MekSection label="Pendapatan" />
          {s.pendapatan.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="pl-revenue" />)}
          <MekTotal label="Total dari pendapatan" value={st.pendapatan} testid="pl-revenue-total" />
          <MekSection label="Beban pokok pendapatan" />
          {s.beban_pokok.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="pl-cogs" />)}
          <MekTotal label="Total dari beban pokok pendapatan" value={st.beban_pokok} testid="pl-cogs-total" />
          <MekTotal label="Laba kotor" value={data.gross_profit} level={2} testid="pl-gross-profit" />
          <MekSection label="Beban operasional" />
          {s.beban_operasional.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="pl-expense" />)}
          <MekTotal label="Total dari beban operasional" value={st.beban_operasional} testid="pl-expense-total" />
          <MekTotal label="Pendapatan operasi" value={data.operating_income} level={2} testid="pl-operating-income" />
          {s.pendapatan_lainnya.length > 0 && (<>
            <MekSection label="Pendapatan lainnya" />
            {s.pendapatan_lainnya.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="pl-oi" />)}
            <MekTotal label="Total dari pendapatan lainnya" value={st.pendapatan_lainnya} testid="pl-oi-total" />
          </>)}
          {s.beban_lainnya.length > 0 && (<>
            <MekSection label="Beban lainnya" />
            {s.beban_lainnya.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="pl-oe" />)}
            <MekTotal label="Total dari beban lainnya" value={st.beban_lainnya} testid="pl-oe-total" />
          </>)}
          <MekTotal label="Laba bersih" value={data.net_income} level={3} testid="pl-net-income" />
        </tbody>
      </table>
    </div>
  );
}

function BalanceSheet({ asOf, onDrill }) {
  const [data, error] = useReport(`/reports/balance-sheet?as_of=${asOf}`, [asOf]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Menyusun neraca...</div>;
  const s = data.sections, st = data.section_totals;
  return (
    <div className="surface workflow-table-wrap" data-testid="balance-sheet-report">
      <table className="workflow-table mek-table">
        <thead><tr><th>NAMA AKUN</th><th className="right">{fmtDMY(asOf)}</th></tr></thead>
        <tbody>
          <MekSection label="Aset" />
          <MekSub label="Aset lancar" />
          {s.current_assets.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="bs-asset" />)}
          <MekTotal label="Total aset lancar" value={st.current_assets} />
          <MekSub label="Aset tetap" />
          {s.fixed_assets.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="bs-asset" />)}
          <MekTotal label="Total aset tetap" value={st.fixed_assets} />
          {s.other_assets.length > 0 && (<>
            <MekSub label="Aset lainnya" />
            {s.other_assets.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="bs-asset" />)}
            <MekTotal label="Total aset lainnya" value={st.other_assets} />
          </>)}
          <MekTotal label="Total aset" value={data.total_assets} level={3} testid="bs-total-assets" />
          <MekSection label="Liabilitas dan modal" />
          <MekSub label="Liabilitas jangka pendek" />
          {s.current_liabilities.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="bs-liability" />)}
          <MekTotal label="Total liabilitas jangka pendek" value={st.current_liabilities} />
          {s.long_term_liabilities.length > 0 && (<>
            <MekSub label="Liabilitas jangka panjang" />
            {s.long_term_liabilities.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="bs-liability" />)}
            <MekTotal label="Total liabilitas jangka panjang" value={st.long_term_liabilities} />
          </>)}
          <MekTotal label="Total liabilitas" value={data.total_liabilities} level={2} testid="bs-total-liabilities" />
          <MekSub label="Modal pemilik" />
          {s.equity.map((r) => <MekRow key={r.code} row={r} onDrill={onDrill} prefix="bs-equity" />)}
          <MekRow row={{ code: "", name: "Pendapatan sampai Tahun lalu", amount: data.earnings_prior }} onDrill={onDrill} prefix="bs-earnings-prior" />
          <MekRow row={{ code: "", name: "Pendapatan Periode ini", amount: data.earnings_current }} onDrill={onDrill} prefix="bs-earnings-current" />
          <MekTotal label="Total modal pemilik" value={data.total_equity_full} level={2} testid="bs-total-equity" />
          <MekTotal label="Total liabilitas dan modal" value={data.total_liabilities_equity} level={3} testid="bs-total-liabilities-equity" />
        </tbody>
      </table>
      <div className={`journal-balance ${data.balanced ? "ok" : "bad"} mek-balance`} data-testid="bs-balanced-indicator">
        <Scale size={14} /> {data.balanced ? "Neraca seimbang" : "Neraca tidak seimbang — periksa jurnal Anda"}
      </div>
    </div>
  );
}

function TrialBalance({ start, end, onDrill }) {
  const [data, error] = useReport(`/reports/trial-balance?start=${start}&end=${end}`, [start, end]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Menyusun neraca saldo...</div>;
  return (
    <div className="surface workflow-table-wrap" data-testid="trial-balance-report">
      <table className="workflow-table">
        <thead><tr><th>KODE</th><th>NAMA AKUN</th><th>KATEGORI</th><th className="right">DEBIT</th><th className="right">KREDIT</th><th /></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.code} data-testid={`tb-row-${r.code}`} onClick={() => onDrill(r.code)}>
              <td className="primary-cell">{r.code}</td>
              <td>{r.name}</td>
              <td>{r.category}</td>
              <td className="right mono">{r.debit ? money(r.debit) : "—"}</td>
              <td className="right mono">{r.credit ? money(r.credit) : "—"}</td>
              <td><button className="table-action" data-testid={`tb-drill-${r.code}`} onClick={(e) => { e.stopPropagation(); onDrill(r.code); }}>Buku besar</button></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="report-total-row">
            <td colSpan={3}><strong>TOTAL</strong></td>
            <td className="right mono" data-testid="tb-total-debit"><strong>{money(data.total_debit)}</strong></td>
            <td className="right mono" data-testid="tb-total-credit"><strong>{money(data.total_credit)}</strong></td>
            <td />
          </tr>
        </tfoot>
      </table>
      <div className={`journal-balance ${data.balanced ? "ok" : "bad"}`} data-testid="tb-balanced-indicator">
        {data.balanced ? "Neraca saldo seimbang" : "Neraca saldo tidak seimbang"}
      </div>
      {data.rows.length === 0 && <div className="empty-state" data-testid="tb-empty-state"><strong>Tidak ada mutasi</strong><span>Tidak ada akun dengan transaksi pada periode ini.</span></div>}
    </div>
  );
}

function GeneralLedger({ start, end, account, setAccount }) {
  const [accounts, setAccounts] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [journal, setJournal] = useState(null);
  useEffect(() => { api("/accounts").then(setAccounts).catch((e) => setError(e.message)); }, []);
  useEffect(() => {
    let active = true;
    setData(null); setError("");
    if (account) api(`/reports/general-ledger?account_code=${account}&start=${start}&end=${end}`).then((d) => active && setData(d)).catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, [account, start, end]);
  const openJournal = (id) => api(`/journals/${id}`).then(setJournal).catch((e) => setError(e.message));
  return (
    <div data-testid="general-ledger-report">
      {error && <div className="success-message error" data-testid="gl-error-message">{error}</div>}
      <div className="workflow-toolbar surface">
        <label className="gl-account-picker">Akun
          <select data-testid="gl-account-select" value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">Pilih akun...</option>
            {accounts.map((a) => <option key={a.code} value={a.code}>{`${a.code} · ${a.name}`}</option>)}
          </select>
        </label>
        {data && <span className="record-count" data-testid="gl-closing-balance">Saldo akhir: {money(data.closing_balance)}</span>}
      </div>
      {!account && <div className="empty-state surface" data-testid="gl-empty-state"><ArrowLeft size={22} /><strong>Pilih akun</strong><span>Pilih akun untuk melihat mutasi buku besarnya.</span></div>}
      {account && data && (
        <div className="surface workflow-table-wrap">
          <table className="workflow-table">
            <thead><tr><th>TANGGAL</th><th>NOMOR</th><th>REFERENSI</th><th>DESKRIPSI</th><th className="right">DEBIT</th><th className="right">KREDIT</th><th className="right">SALDO</th></tr></thead>
            <tbody>
              <tr className="gl-opening-row" data-testid="gl-opening-row">
                <td colSpan={6}><strong>Saldo awal</strong></td>
                <td className="right mono">{money(data.opening_balance)}</td>
              </tr>
              {data.entries.map((e, i) => (
                <tr key={i} data-testid={`gl-entry-${i}`} onClick={() => openJournal(e.journal_id)}>
                  <td>{e.date}</td>
                  <td className="primary-cell">{e.number}</td>
                  <td>{e.reference || "—"}</td>
                  <td>{e.description}</td>
                  <td className="right mono">{e.debit ? money(e.debit) : "—"}</td>
                  <td className="right mono">{e.credit ? money(e.credit) : "—"}</td>
                  <td className="right mono">{money(e.balance)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="report-total-row">
                <td colSpan={4}><strong>TOTAL MUTASI</strong></td>
                <td className="right mono" data-testid="gl-total-debit"><strong>{money(data.total_debit)}</strong></td>
                <td className="right mono" data-testid="gl-total-credit"><strong>{money(data.total_credit)}</strong></td>
                <td className="right mono" data-testid="gl-closing-cell"><strong>{money(data.closing_balance)}</strong></td>
              </tr>
            </tfoot>
          </table>
          {data.entries.length === 0 && <div className="empty-state" data-testid="gl-no-entries"><strong>Tidak ada mutasi</strong><span>Tidak ada transaksi pada periode ini.</span></div>}
        </div>
      )}
      {journal && (
        <>
          <div className="drawer-backdrop" onClick={() => setJournal(null)} />
          <div className="detail-drawer" data-testid="gl-journal-drawer">
            <div className="drawer-head">
              <div><p className="eyebrow">DETAIL JURNAL</p><h2>{journal.number}</h2></div>
              <button data-testid="gl-journal-close" className="icon-btn" onClick={() => setJournal(null)}><X size={18} /></button>
            </div>
            <div className="detail-status"><span className="status posted">{journal.status}</span><span className="muted">{journal.date}</span></div>
            <div className="detail-fields">
              <div><small>deskripsi</small><strong>{journal.description}</strong></div>
              <div><small>referensi</small><strong>{journal.reference || "—"}</strong></div>
            </div>
            <div className="journal-lines">
              <h3>Baris jurnal</h3>
              {(journal.lines || []).map((l, i) => <div key={i}><span>{l.account_code} · {l.description}</span><b>{money(l.debit || l.credit)}</b></div>)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const SECTION_LABELS = { operating: "Aktivitas Operasional", investing: "Aktivitas Investasi", financing: "Aktivitas Pendanaan" };

function CashFlow({ start, end }) {
  const [data, error] = useReport(`/reports/cash-flow?start=${start}&end=${end}`, [start, end]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Menyusun arus kas...</div>;
  return (
    <div className="surface report-card" data-testid="cash-flow-report">
      <div className="report-row static"><span><em>—</em>Saldo Kas Awal Periode</span><strong data-testid="cf-opening">{money(data.opening_cash)}</strong><i /></div>
      {Object.entries(SECTION_LABELS).map(([key, label]) => (
        <section key={key}>
          <h3>{label}</h3>
          {data.sections[key].length === 0 && <p className="muted report-empty">Tidak ada arus kas.</p>}
          {data.sections[key].map((r, i) => (
            <div className="report-row static" key={i} data-testid={`cf-${key}-${i}`}>
              <span><em>{r.date}</em>{r.number} · {r.description || r.reference || ""}</span>
              <strong className={r.amount < 0 ? "negative-amount" : ""}>{money(r.amount)}</strong><i />
            </div>
          ))}
          <div className="report-subtotal"><span>Kas Bersih {label}</span><strong data-testid={`cf-total-${key}`}>{money(data.totals[key])}</strong></div>
        </section>
      ))}
      <div className={`report-highlight ${data.net_change < 0 ? "negative" : ""}`} data-testid="cf-net-change"><span>Kenaikan (Penurunan) Kas Bersih</span><strong>{money(data.net_change)}</strong></div>
      <div className="report-highlight strong" data-testid="cf-ending"><span>Saldo Kas Akhir Periode</span><strong>{money(data.ending_cash)}</strong></div>
    </div>
  );
}

function PartySummary({ path, start, end, partyKey, partyLabel, testid }) {
  const [data, error] = useReport(`/reports/${path}?start=${start}&end=${end}`, [start, end]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Menyusun laporan...</div>;
  return (
    <div className="surface workflow-table-wrap" data-testid={testid}>
      <table className="workflow-table">
        <thead><tr><th>{partyLabel}</th><th className="right">JUMLAH TRANSAKSI</th><th className="right">TOTAL (IDR)</th><th className="right">TERBAYAR (IDR)</th><th className="right">SISA (IDR)</th></tr></thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i} data-testid={`${testid}-row-${i}`}>
              <td className="primary-cell">{r[partyKey]}</td>
              <td className="right mono">{r.count}</td>
              <td className="right mono">{money(r.total)}</td>
              <td className="right mono">{money(r.paid)}</td>
              <td className="right mono">{money(r.outstanding)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="report-total-row">
            <td><strong>TOTAL</strong></td><td />
            <td className="right mono" data-testid={`${testid}-grand-total`}><strong>{money(data.grand_total)}</strong></td><td />
            <td className="right mono" data-testid={`${testid}-grand-outstanding`}><strong>{money(data.grand_outstanding)}</strong></td>
          </tr>
        </tfoot>
      </table>
      {data.rows.length === 0 && <div className="empty-state"><strong>Tidak ada transaksi</strong><span>Tidak ada data pada periode ini.</span></div>}
    </div>
  );
}

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function TaxPPN() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, error] = useReport(`/reports/tax-ppn?year=${year}&month=${month}`, [year, month]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Menghitung PPN masa...</div>;
  const statusLabel = data.status === "KURANG_BAYAR" ? "PPN Kurang Bayar (harus disetor)" : data.status === "LEBIH_BAYAR" ? "PPN Lebih Bayar (dapat dikompensasi)" : "Nihil";
  const exportCsv = () => {
    const header = "Tanggal;Nomor Jurnal;Referensi;Deskripsi;PPN Keluaran;PPN Masukan";
    const lines = data.transactions.map((t) => [t.date, t.number, t.reference || "", (t.description || "").replace(/;/g, ","), t.keluaran, t.masukan].join(";"));
    const blob = new Blob(["\uFEFF" + [header, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `rekap-ppn-${data.masa}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div data-testid="tax-ppn-report">
      <div className="workflow-toolbar surface prod-toolbar">
        <label className="gl-account-picker">Masa pajak
          <select data-testid="ppn-month-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="gl-account-picker">Tahun
          <select data-testid="ppn-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year - 2, year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <button data-testid="ppn-export-csv" className="secondary-btn small" onClick={exportCsv}><Download size={14} /> Ekspor CSV (persiapan e-Faktur)</button>
      </div>
      <div className="exp-cards" data-testid="ppn-summary-cards">
        <div className="exp-card"><div className="exp-card-head"><span>PPN Keluaran (Pajak Penjualan)</span></div><div className="exp-card-body"><small>Masa {MONTHS[month - 1]} {year}</small><strong data-testid="ppn-keluaran">{money(data.keluaran)}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>PPN Masukan (Pajak Pembelian)</span></div><div className="exp-card-body"><small>Masa {MONTHS[month - 1]} {year}</small><strong data-testid="ppn-masukan">{money(data.masukan)}</strong></div></div>
        <div className="exp-card"><div className="exp-card-head"><span>{data.net >= 0 ? "PPN Kurang Bayar" : "PPN Lebih Bayar"}</span></div><div className="exp-card-body"><small>{statusLabel}</small><strong data-testid="ppn-net" className={data.net < 0 ? "negative-amount" : ""}>{money(Math.abs(data.net))}</strong></div></div>
      </div>
      <div className="surface ppn-steps" data-testid="ppn-filing-steps">
        <h3>Persiapan pelaporan SPT Masa PPN — {MONTHS[month - 1]} {year}</h3>
        <ol>
          <li>Periksa rekap transaksi PPN di bawah dan cocokkan dengan faktur pajak Anda.</li>
          <li>Ekspor CSV sebagai lampiran kertas kerja rekonsiliasi e-Faktur.</li>
          <li>Setor PPN kurang bayar (jika ada) via kode billing DJP sebelum <b>{data.due_date?.split("-").reverse().join("-")}</b>.</li>
          <li>Lapor SPT Masa PPN melalui aplikasi e-Faktur / Coretax DJP paling lambat akhir bulan berikutnya.</li>
        </ol>
      </div>
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="ppn-transactions-table">
          <thead><tr><th>TANGGAL</th><th>NOMOR JURNAL</th><th>REFERENSI</th><th>DESKRIPSI</th><th className="right">PPN KELUARAN</th><th className="right">PPN MASUKAN</th></tr></thead>
          <tbody>
            {data.transactions.map((t, i) => (
              <tr key={i} data-testid={`ppn-tx-${i}`}>
                <td>{t.date}</td><td className="primary-cell">{t.number}</td><td>{t.reference || "—"}</td><td>{t.description || "—"}</td>
                <td className="right mono">{t.keluaran ? money(t.keluaran) : "—"}</td>
                <td className="right mono">{t.masukan ? money(t.masukan) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.transactions.length === 0 && <div className="empty-state" data-testid="ppn-empty"><strong>Tidak ada transaksi PPN</strong><span>Tidak ada mutasi PPN pada masa ini.</span></div>}
      </div>
      <div className="surface workflow-table-wrap ppn-monthly">
        <table className="workflow-table" data-testid="ppn-monthly-table">
          <thead><tr><th>MASA {year}</th><th className="right">PPN KELUARAN</th><th className="right">PPN MASUKAN</th><th className="right">KURANG / (LEBIH) BAYAR</th></tr></thead>
          <tbody>
            {data.monthly.map((m, i) => (
              <tr key={m.masa} className={m.masa === data.masa ? "recon-row-matched" : ""} data-testid={`ppn-month-${i}`}>
                <td className="primary-cell">{MONTHS[i]}</td>
                <td className="right mono">{money(m.keluaran)}</td>
                <td className="right mono">{money(m.masukan)}</td>
                <td className={`right mono ${m.net < 0 ? "negative-amount" : ""}`}>{money(m.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EquityChanges({ start, end, onDrill }) {
  const [data, error] = useReport(`/reports/equity-changes?start=${start}&end=${end}`, [start, end]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Menghitung perubahan modal...</div>;
  return (
    <div className="surface report-card" data-testid="equity-changes-report">
      <table className="workflow-table" data-testid="ec-table">
        <thead><tr><th>KODE</th><th>NAMA AKUN</th><th className="right">PERMULAAN</th><th className="right">DEBIT</th><th className="right">KREDIT</th><th className="right">SALDO AKHIR</th></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.code} className="ec-row" onClick={() => onDrill(r.code)} data-testid={`ec-row-${r.code}`}>
              <td className="mono">{r.code}</td><td>{r.name}</td>
              <td className="right mono">{money(r.opening)}</td><td className="right mono">{money(r.debit)}</td>
              <td className="right mono">{money(r.credit)}</td><td className="right mono">{money(r.ending)}</td>
            </tr>
          ))}
          <tr><td /><td>Laba Ditahan s/d Periode Lalu</td><td className="right mono">{money(data.retained_prior)}</td><td /><td /><td className="right mono">{money(data.retained_prior)}</td></tr>
          <tr><td /><td>Laba (Rugi) Periode Berjalan</td><td /><td /><td /><td className="right mono">{money(data.income_period)}</td></tr>
          <tr className="ec-total" data-testid="ec-total-row">
            <td /><td><b>TOTAL MODAL</b></td>
            <td className="right mono"><b>{money(data.total.opening + data.retained_prior)}</b></td>
            <td className="right mono"><b>{money(data.total.debit)}</b></td>
            <td className="right mono"><b>{money(data.total.credit)}</b></td>
            <td className="right mono"><b>{money(data.grand_ending)}</b></td>
          </tr>
        </tbody>
      </table>
      {data.rows.length === 0 && <p className="muted report-empty">Belum ada transaksi ekuitas pada periode ini.</p>}
    </div>
  );
}

function JournalReport({ start, end }) {
  const [data, error] = useReport(`/journals`, [start, end]);
  if (error) return <div className="success-message error">{error}</div>;
  if (!data) return <div className="report-loading" data-testid="report-loading">Memuat jurnal...</div>;
  const rows = data.filter((j) => j.status === "POSTED" && (!start || j.date >= start) && (!end || j.date <= end));
  const totalD = rows.reduce((s, j) => s + (j.total_debit || 0), 0);
  return (
    <div className="surface report-card" data-testid="journal-report">
      <table className="workflow-table" data-testid="jr-table">
        <thead><tr><th>TANGGAL</th><th>NOMOR</th><th>DESKRIPSI</th><th>REFERENSI</th><th className="right">DEBIT</th><th className="right">KREDIT</th></tr></thead>
        <tbody>
          {rows.slice(0, 300).map((j) => (
            <tr key={j.id}>
              <td>{(j.date || "").split("-").reverse().join("/")}</td>
              <td className="primary-cell">{j.number}</td>
              <td>{j.description || "—"}</td>
              <td className="mono">{j.reference || "—"}</td>
              <td className="right mono">{money(j.total_debit)}</td>
              <td className="right mono">{money(j.total_credit)}</td>
            </tr>
          ))}
          <tr className="ec-total"><td colSpan={4}><b>TOTAL ({rows.length} jurnal)</b></td><td className="right mono"><b>{money(totalD)}</b></td><td className="right mono"><b>{money(totalD)}</b></td></tr>
        </tbody>
      </table>
      {rows.length === 0 && <p className="muted report-empty">Belum ada jurnal terposting pada periode ini.</p>}
    </div>
  );
}
